/**
 * tx.tsx — the row-level pieces shared by the home screen, the transactions screen and the
 * card panels: the category chip (editable), the amount cell (sign + arrow, never colour
 * alone), the foreign-currency line and the compact "recent transaction" row.
 *
 * REDESIGN §7b: a transaction is drawn with the glyph of its Financy category (`main`/`sub`),
 * not of its display group — the kit's table covers every key of the live taxonomy, so a
 * "דלק" row gets a fuel pump rather than the group's generic car. Rows that are not spending
 * (income, a returned standing order, an internal transfer, the monthly card charge) carry the
 * semantic glyph for what they are. Nothing ever renders blank.
 */

import type { TransactionRow } from '../../../shared-types.ts';
import { fmtDM, fmtForeign, fmtMoney } from '../format.ts';
import { CATEGORY, he } from '../i18n/he.ts';
import { buildPath } from '../router.ts';
import { Link } from '../hooks/useRouter.tsx';
import { Icon } from './Icon.tsx';
import { Chip, Ltr } from './ui.tsx';
import { CategoryIcon as KitCategoryIcon, ProviderMark } from '../../../../shared/ui/src/index.tsx';
import type { GlyphName } from '../../../../shared/ui/src/index.tsx';

export function isFlowRow(row: TransactionRow): boolean {
  return (row.scope === 'spend' || row.scope === 'income') && !row.internal;
}

export function rowIconName(row: TransactionRow): string {
  if (row.scope === 'income') return 'upRight';
  if (row.returnKind) return 'repeat';
  if (row.internal || row.scope === 'transfer' || row.scope === 'card-charge') return 'repeat';
  return CATEGORY[row.groupKey]?.icon ?? 'dashed';
}

/** The glyph a non-spending row overrides its category with; `undefined` = use the category. */
function scopeGlyph(row: TransactionRow): GlyphName | undefined {
  if (row.scope === 'income') return 'banknote';
  if (row.returnKind) return 'undo';
  if (row.scope === 'card-charge') return 'card';
  if (row.internal || row.scope === 'transfer') return 'transfer';
  return undefined;
}

/** The drawn 20px glyph of one transaction, on its category's soft hue (REDESIGN §7b). */
export function TxIcon({ row, size = 20 }: { row: TransactionRow; size?: number }) {
  const glyph = scopeGlyph(row);
  return (
    <KitCategoryIcon
      main={row.category.main}
      sub={row.category.sub}
      glyph={glyph}
      hue={glyph === undefined ? undefined : 'neutral'}
      soft
      size={size}
    />
  );
}

/** The provider mark that prefixes an account or card in a row (REDESIGN §7a). */
export function TxProvider({ row, size = 20 }: { row: TransactionRow; size?: 20 | 28 | 40 }) {
  return <ProviderMark providerId={row.account.providerId} size={size} />;
}

export function ForeignLine({ row }: { row: TransactionRow }) {
  if (!row.origCurrency || row.origCurrency === row.currency) return null;
  return <span className="fx">{fmtForeign(row.origAmountMinor, row.origCurrency)}</span>;
}

export function Amount({ row, decimals = 2 }: { row: TransactionRow; decimals?: number }) {
  if (row.amountAgorot === null) {
    return (
      <span className="num tab t3">
        {fmtForeign(row.amountMinor, row.currency)} <Chip tone="muted">{he.tx.foreignChip}</Chip>
      </span>
    );
  }
  const positive = row.amountAgorot > 0;
  return (
    <span className={`num tab ${positive ? 'good' : 't2'}`}>
      <Icon name={positive ? 'upRight' : 'downLeft'} className="xs" />
      {fmtMoney(row.amountAgorot, decimals, true)}
    </span>
  );
}

/** Chip that shows the resolved category — clickable opens the category picker. */
export function CategoryChip({ row, onEdit }: { row: TransactionRow; onEdit?: (anchor: HTMLElement) => void }) {
  if (row.scope === 'income') return <Chip tone="good" icon="upRight">{he.tx.incomeChip}</Chip>;
  if (row.returnKind) return <Chip tone="warn" icon="repeat">{he.tx.returnChip}</Chip>;
  if (row.scope === 'card-charge') return <Chip tone="muted" icon="repeat">{he.tx.cardChargeChip}</Chip>;
  if (row.internal || row.scope === 'transfer') return <Chip tone="muted" icon="repeat">{he.tx.internalChip}</Chip>;
  const label = CATEGORY[row.groupKey]?.label ?? CATEGORY.OTHER.label;
  if (!onEdit) return <Chip tone="muted">{label}</Chip>;
  return (
    <button
      type="button"
      className="chip btn-chip"
      aria-label={he.tx.categoryAria(label)}
      title={row.userCategory ? he.tx.changedBy(row.userCategory.by, fmtDM(row.userCategory.at.slice(0, 10))) : undefined}
      onClick={(event) => {
        event.stopPropagation();
        onEdit(event.currentTarget);
      }}
    >
      <KitCategoryIcon groupKey={row.groupKey} size={14} />
      {label}
      {row.userCategory ? <span className="cat-dot" aria-hidden="true" /> : null}
    </button>
  );
}

export function PendingChip({ row }: { row: TransactionRow }) {
  if (row.status !== 'PENDING') return null;
  return <Chip tone="warn" icon="clock">{he.tx.pending}</Chip>;
}

/** The compact row used by "5 עסקאות אחרונות" and "חיובים אחרונים". */
export function RecentRow({ row }: { row: TransactionRow }) {
  const accountLabel = row.account.type === 'CARD' ? `${row.account.name} ${row.account.numberLast4 ? `****${row.account.numberLast4}` : ''}` : row.account.name;
  const categoryLabel = row.scope === 'income'
    ? he.tx.incomeChip
    : row.returnKind
      ? he.tx.returnChip
      : row.internal || row.scope === 'transfer'
        ? he.tx.internalChip
        : CATEGORY[row.groupKey]?.label;
  return (
    <Link className="listrow clickable rowlink" to={buildPath('transactions', null, { tx: row.id })}>
      <TxIcon row={row} />
      <span className="main">
        <span className="name">{row.merchant}</span>
        <span className="sub">
          <TxProvider row={row} />
          <span className="num">{fmtDM(row.effDate)}</span> · <Ltr>{accountLabel}</Ltr>
          {categoryLabel ? ` · ${categoryLabel}` : ''}
        </span>
      </span>
      <span className="amt">
        <Amount row={row} />
        {row.status === 'PENDING' ? (
          <>
            <br />
            <PendingChip row={row} />
          </>
        ) : null}
      </span>
    </Link>
  );
}
